import React, { RefObject, useCallback, useRef, useState } from 'react';
import type { IPoint, IStroke, IWorkspaceView, TAnnotationDialogMode } from '@datatype/gridgio/type';

interface IWorkspaceGestureProps {
  viewportRef: RefObject<HTMLDivElement | null>;
  moveActive: boolean;
  handleViewChange: React.Dispatch<React.SetStateAction<IWorkspaceView>>;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;
const DEFAULT_VIEW: IWorkspaceView = { zoom: 1, x: 0, y: 0 };

const getDistance = (first: IPoint, second: IPoint) => Math.hypot(second.x - first.x, second.y - first.y);

const getCenter = (first: IPoint, second: IPoint): IPoint => ({
  x: (first.x + second.x) / 2,
  y: (first.y + second.y) / 2,
});

export const useWorkspaceView = () => {
  const [view, setView] = useState<IWorkspaceView>(DEFAULT_VIEW);
  const [moveActive, setMoveActive] = useState(false);

  const resetView = useCallback(() => setView(DEFAULT_VIEW), []);
  const disableMove = useCallback(() => setMoveActive(false), []);

  const handleToolbarClick = useCallback((action: string) => {
    if (action === 'zoom-out') {
      setView((current) => ({ ...current, zoom: Math.max(MIN_ZOOM, current.zoom - ZOOM_STEP) }));
    } else if (action === 'zoom-in') {
      setView((current) => ({ ...current, zoom: Math.min(MAX_ZOOM, current.zoom + ZOOM_STEP) }));
    } else if (action === 'fit') {
      setView(DEFAULT_VIEW);
    } else if (action === 'move') {
      setMoveActive((active) => !active);
    }
  }, []);

  return { view, setView, moveActive, resetView, disableMove, handleToolbarClick };
};

export const useWorkspaceAnnotation = () => {
  const [drawingIssueId, setDrawingIssueId] = useState<string | null>(null);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [memoStroke, setMemoStroke] = useState<IStroke[]>([]);
  const [selectedMemoId, setSelectedMemoId] = useState<string | null>(null);
  const [dialogMode, setDialogMode] = useState<TAnnotationDialogMode | null>(null);

  const reset = useCallback(() => {
    setDrawingIssueId(null);
    setSelectedAnnotationId(null);
    setMemoStroke([]);
    setSelectedMemoId(null);
    setDialogMode(null);
  }, []);

  const toggleDrawingIssue = useCallback((issueId: string) => {
    setDrawingIssueId((current) => (current === issueId ? null : issueId));
    setSelectedAnnotationId(null);
    setDialogMode(null);
  }, []);

  const completeDrawing = useCallback((annotationId: string) => {
    setDrawingIssueId(null);
    setSelectedAnnotationId(annotationId);
    setMemoStroke([]);
    setSelectedMemoId(null);
    setDialogMode('property');
  }, []);

  const openDialog = useCallback(
    (mode: TAnnotationDialogMode, stroke: IStroke[] = [], memoId: string | null = null) => {
      setMemoStroke(stroke);
      setSelectedMemoId(memoId);
      setDialogMode(mode);
    },
    [],
  );

  const completeDialog = useCallback(() => {
    setDialogMode(null);
    setSelectedMemoId(null);
  }, []);

  const selectAnnotation = useCallback((annotationId: string | null) => {
    setDrawingIssueId(null);
    setSelectedAnnotationId(annotationId);
    setDialogMode(null);
    setSelectedMemoId(null);
  }, []);

  return {
    drawingIssueId,
    selectedAnnotationId,
    memoStroke,
    selectedMemoId,
    dialogMode,
    setMemoStroke,
    setDialogMode,
    reset,
    toggleDrawingIssue,
    completeDrawing,
    openDialog,
    completeDialog,
    selectAnnotation,
  };
};

export const useWorkspaceGesture = ({ viewportRef, moveActive, handleViewChange }: IWorkspaceGestureProps) => {
  const pointersRef = useRef(new Map<number, IPoint>());
  const previousCenterRef = useRef<IPoint | null>(null);
  const previousDistanceRef = useRef<number | null>(null);

  const updatePreviousGesture = () => {
    const pointers = Array.from(pointersRef.current.values());
    previousCenterRef.current =
      pointers.length === 0 ? null : pointers.length === 1 ? pointers[0] : getCenter(pointers[0], pointers[1]);
    previousDistanceRef.current = pointers.length > 1 ? getDistance(pointers[0], pointers[1]) : null;
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // 손가락·펜·마우스 모두 손바닥 도구가 켜져 있을 때만 이동·확대를 처리한다.
    if (!moveActive) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    updatePreviousGesture();
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    event.preventDefault();
    event.stopPropagation();
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const pointers = Array.from(pointersRef.current.values());
    const center = pointers.length === 1 ? pointers[0] : getCenter(pointers[0], pointers[1]);
    const previousCenter = previousCenterRef.current;
    if (!previousCenter) return;

    if (pointers.length === 1) {
      handleViewChange((current) => ({
        ...current,
        x: current.x + center.x - previousCenter.x,
        y: current.y + center.y - previousCenter.y,
      }));
    } else {
      const distance = getDistance(pointers[0], pointers[1]);
      const previousDistance = previousDistanceRef.current || distance;
      const viewport = viewportRef.current?.getBoundingClientRect();
      if (viewport) {
        handleViewChange((current) => {
          const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current.zoom * (distance / previousDistance)));
          const previousX = previousCenter.x - viewport.left - viewport.width / 2;
          const previousY = previousCenter.y - viewport.top - viewport.height / 2;
          const nextX = center.x - viewport.left - viewport.width / 2;
          const nextY = center.y - viewport.top - viewport.height / 2;
          return {
            zoom,
            x: nextX - ((previousX - current.x) / current.zoom) * zoom,
            y: nextY - ((previousY - current.y) / current.zoom) * zoom,
          };
        });
      }
      previousDistanceRef.current = distance;
    }
    previousCenterRef.current = center;
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    event.preventDefault();
    event.stopPropagation();
    pointersRef.current.delete(event.pointerId);
    updatePreviousGesture();
  };

  return { handlePointerDown, handlePointerMove, handlePointerEnd };
};
